import { useNavigate } from 'react-router-dom'
import React, { useRef } from 'react'
import { useProjectContext } from '../../../context/ProjectContext'
import { useEffect, useState } from 'react'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import { StandardButtonBrand } from '../../ui_reusable/styles/Button.styles'
import {
  SolutionPoster,
  SolutionPosterContainer,
  SolutionPosterTitle,
  ActionButtonsContainer,
} from './styles/NewModel.styles'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { Message } from 'rsuite'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import { VerySmallNormalTag } from '../../ui_reusable/styles/Tags.styles'

import theme from '../../../styles/theme'
import { useClientContext } from '../../../context/ClientContext'
import {
  selectedProjectAllowsModelRun,
  useClientProjectContext,
} from '../../../context/ClientProjectContext'
import ModelLoadingModal from '../comp_reusable/LoadingModelModal'
import DeleteModel from '../comp_reusable/DeleteModel'
import { getFitnessStringByScore } from '../../../utils/ScoreFitnessValidation'
import { ROUTED_REPORT_MODES } from '../../reports/types/ReportTypes'

const NewModelPage = () => {
  const navigate = useNavigate()
  const [solutions, setSolutions] = useState([])
  const [projectSolutions, setProjectSolutions] = useState([])
  const { selectedProject } = useProjectContext()
  const { selectedClient } = useClientContext()
  const { clientProjectData } = useClientProjectContext()
  const canRunModels = selectedProjectAllowsModelRun(
    selectedProject,
    clientProjectData,
  )
  const { pushToast } = useStandardToaster()
  const [artifacts, setArtifacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [refresh, setRefresh] = useState(new Date().getTime())

  const [run, setRun] = useState(false)
  const [progress, setProgress] = useState(0)
  const abortControllerRef = useRef(null)
  const [pendingEditNavigation, setPendingEditNavigation] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedModelToDelete, setSelectedModelToDelete] = useState(null)
  const [isDuplicating, setIsDuplicating] = useState(false)

  const handleClose = () => {
    setRun(false)
    setProgress(0)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  const handleDeleteClick = (rowData) => {
    setSelectedModelToDelete(rowData)
    setDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    setSelectedModelToDelete(null)
  }

  useEffect(() => {
    const getSolutions = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/solutions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      const res = await response.json()

      setSolutions(res.data)
    }

    const getProjectSolutions = async () => {
      if (selectedProject) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/${selectedProject.id}/solutions/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )
        const res = await response.json()

        setProjectSolutions(res.data)
      }
    }

    getSolutions()
    getProjectSolutions()
  }, [selectedProject])

  const handleClick = (solutionId) => {
    const isSolutionPresent = projectSolutions.some(
      (elem) => elem.solution.id === solutionId,
    )

    if (isSolutionPresent) {
      navigate('/models/build', { state: { solutionId: solutionId } })
    }
  }

  // Fetch artifacts - IMPORTANT: Keep page_size=5 and ordering parameters
  // FIX: Added AbortController to prevent race condition where a slow unfiltered request
  // could complete after a faster filtered request, overwriting correct data with stale data.
  // When selectedClient/selectedProject change, the previous request is aborted.
  useEffect(() => {
    const abortController = new AbortController()

    const fetchArtifacts = async () => {
      setLoading(true)
      try {
        let url = `${process.env.REACT_APP_API_URL}/api/v2/artifacts/?limit=5&ordering=-id`

        if (selectedClient) {
          url += `&client_id=${selectedClient.id}`
        }

        if (selectedProject) {
          url += `&project_id=${selectedProject.id}`
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          signal: abortController.signal,
        })
        const result = await response.json()

        setArtifacts(result.data)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchArtifacts()

    return () => {
      abortController.abort()
    }
  }, [selectedClient, selectedProject, refresh])

  useEffect(() => {
    if (pendingEditNavigation) {
      navigate('/models/build', {
        state: {
          solutionId:
            pendingEditNavigation.artifact.project_solution.solution.id,
          artifact: pendingEditNavigation.artifact,
          mode: pendingEditNavigation.mode,
          origin: pendingEditNavigation.origin,
        },
      })
      setPendingEditNavigation(null)
    }
  }, [pendingEditNavigation, navigate])

  const handleDuplicate = async (modelId) => {
    try {
      setIsDuplicating(true)

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/duplicate/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ artifact_id: modelId }),
        },
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to duplicate model')
      }

      // Refresh the model list
      setRefresh(new Date().getTime())

      pushToast(
        <Message type="success">Model duplicated successfully</Message>,
        { placement: 'topCenter', duration: 3000 },
      )
    } catch (error) {
      console.error('Error duplicating model:', error)
      pushToast(
        <Message type="error">
          Failed to duplicate model: {error.message}
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } finally {
      setIsDuplicating(false)
    }
  }

  const handleEdit = async (flattenedArtifact) => {
    const originalArtifact = artifacts.find(
      (a) => a.id === flattenedArtifact.id,
    )

    if (!originalArtifact) {
      console.error('Original artifact not found for ID:', flattenedArtifact.id)
      pushToast(
        <Message type="error">
          Could not edit model. Original data not found.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const solutionName = (
      originalArtifact.solution_type ||
      originalArtifact.project_solution?.solution?.name ||
      'impact'
    ).toLowerCase()
    const detailPath =
      solutionName === 'optimize'
        ? `optimize/${originalArtifact.id}`
        : `impact/${originalArtifact.id}`

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${detailPath}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!response.ok) {
        throw new Error('Failed to load artifact detail')
      }
      const result = await response.json()
      const detailArtifact = result.data || result

      setPendingEditNavigation({
        artifact: detailArtifact,
        mode: ROUTED_REPORT_MODES.EDIT_MODEL,
        origin: '/models/new',
      })
    } catch (error) {
      console.error(error)
      pushToast(
        <Message type="error">
          Could not load model details for editing.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleRun = async (artifactId) => {
    if (!canRunModels) {
      return
    }
    setRun(true)
    let currentProgress = 0
    const interval = setInterval(() => {
      currentProgress += 10
      setProgress(currentProgress)
      if (currentProgress >= 90) clearInterval(interval) // Stop near completion
    }, 300)
    const listArtifact = artifacts.find((elem) => elem.id === artifactId)
    if (!listArtifact) {
      clearInterval(interval)
      setRun(false)
      pushToast(
        <Message type="error">Could not find model to rerun.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const solutionKey = (
      listArtifact.solution_type ||
      listArtifact.project_solution?.solution?.name ||
      'impact'
    ).toLowerCase()
    const detailPath =
      solutionKey === 'optimize'
        ? `optimize/${listArtifact.id}`
        : `impact/${listArtifact.id}`

    let artifact
    try {
      const detailResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/${detailPath}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      if (!detailResponse.ok) {
        throw new Error('Failed to load artifact detail')
      }
      const detailResult = await detailResponse.json()
      artifact = detailResult.data || detailResult
    } catch (error) {
      console.error(error)
      clearInterval(interval)
      setRun(false)
      pushToast(
        <Message type="error">Could not load model details for rerun.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const independentDriverIds = (
      artifact.independent_drivers ||
      []
    ).map((elem) => elem.id)

    const payload = {
      mode: 'rerun',
      id: artifact.id,
      name: artifact.name + '_' + new Date().toISOString().slice(0, 10),
      timeframe: artifact.timeframe,
      project_solution_id: artifact.project_solution?.id,
      dependent_driver_id: artifact.dependent_driver?.id,
      independent_driver_ids: independentDriverIds,
      client_id: artifact.client?.id,
      parameters: {
        model_data_frequency: artifact.parameters?.model_data_frequency,
        business_goal: artifact.parameters?.business_goal ?? 'increase',
        aggregation_method: artifact.parameters?.aggregation_method,
      },
      optimize_config: {
        dependent_driver_is_revenue:
          artifact.optimize_config?.dependent_driver_is_revenue ?? true,
        geo_granularity:
          artifact.optimize_config?.geo_granularity ?? 'national',
        budget_type: artifact.optimize_config?.budget_type ?? 'fixed',
        budget_amount: artifact.optimize_config?.budget_amount ?? null,
        max_lag: artifact.optimize_config?.max_lag ?? 8,
        spend_constraint: artifact.optimize_config?.spend_constraint ?? 0.2,
        hill_before_adstock:
          artifact.optimize_config?.hill_before_adstock ?? false,
        roi_impression: artifact.optimize_config?.roi_impression ?? 'standard',
        roi_reach: artifact.optimize_config?.roi_reach ?? 'standard',
        model_data_frequency: artifact.optimize_config?.model_data_frequency,
      },
      channel_driver_inputs: artifact.channel_drivers?.map((elem) => {
        return {
          channel_id: elem.channel?.id,
          spend_dataset_id: elem.spend_dataset?.id,
          impression_dataset_id: elem?.impression_dataset?.id,
          reach_dataset_id: elem?.reach_dataset?.id,
          frequency_dataset_id: elem?.frequency_dataset?.id,
          adstock_decay: elem?.adstock_decay,
        }
      }),
    }

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Get solution name for routing
    const solutionName =
      artifact.project_solution?.solution?.name?.toLowerCase()

    // Create a new AbortController
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/artifacts/predict/${solutionName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
          signal,
        },
      )

      if (response.ok) {
        const res = await response.json()
        const data = res.data

        setProgress(100)
        setRun(false)

        navigate(`/${solutionName}/report`, {
          state: {
            data: data,
            origin: '/models',
            mode: ROUTED_REPORT_MODES.RERUN_MODEL,
            modelData: artifact,
          },
        })
      } else {
        const errorData = await response.json()

        pushToast(<Message type="error">{errorData.message}</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
        console.error('Failed to run model:', response)
        setRun(false)
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        pushToast(
          <Message type="error">Error running model: {error.message}</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        console.error('Error running model:', error)
      }

      setRun(false)
    }
  }

  const getFlattenedArtifactsData = () => {
    return artifacts.map((model) => {
      // Use getQualityString from ScoreFitnessValidation.js
      const fitScore = model.fit_score || 0
      const qualityStatus = getFitnessStringByScore(fitScore)

      return {
        ...model,
        solution: model.project_solution?.solution?.name || '',
        project: model.project_solution?.project?.name || '',
        client: model.project_solution?.project?.client?.name || '',
        dependent_driver_name: model.dependent_driver?.name || '',
        independent_drivers_names: '',
        created_by: model.created_by?.name || model.created_by,
        kam: model.project_solution?.project?.kam?.name || '',
        // Add searchable quality status string
        fit_score_quality: qualityStatus,
        // Add variables count
        variables_count: model.independent_drivers_count ?? 0,
      }
    })
  }

  const headers = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: false, // No sorting for Dashboard
      isSearchable: false, // No search for Dashboard
    },
    {
      title: 'Model',
      key: 'model',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Last run date',
      key: 'run_at',
      type: 'datetime',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Fit score',
      key: 'fit_score_quality',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Created by',
      key: 'created_by',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Edit /  Run /  Duplicate /  Delete',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  const getSortedSolutions = (solutionsArray) => {
    const orderPriority = ['impact', 'optimize']

    return [...solutionsArray].sort((a, b) => {
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()

      const aIndex = orderPriority.indexOf(aName)
      const bIndex = orderPriority.indexOf(bName)

      // If both solutions are in the priority list, sort by priority
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }

      // If only one is in the priority list, it comes first
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1

      // If neither is in the priority list, keep original order
      return 0
    })
  }

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Build new model</SectionTitle>
        <ClientProjectPicker />
      </HeaderContainer>
      {
        <SimpleAccordion
          id={'dashboardPageAccordion'}
          defaultActiveKey={['select_model']}
        >
          <SimpleAccordion.Panel eventKey="select_model" noCollapse>
            <SolutionPosterContainer>
              {solutions &&
                getSortedSolutions(solutions).map((solution, i) => {
                  const findSolution = projectSolutions?.find(
                    (elem) => elem.solution.id === solution.id,
                  )
                  const solutionActive = findSolution
                    ? findSolution.is_active && selectedProject && canRunModels
                    : false

                  return (
                    <SolutionPoster key={i + 1}>
                      <SolutionPosterTitle>{solution.name}</SolutionPosterTitle>
                      <div style={{ margin: '1.2rem 0' }}>
                        <strong style={{ marginBottom: '0.2rem' }}>
                          {solution.impressum}
                        </strong>
                        <p>{solution.description}</p>
                      </div>
                      <StandardButtonBrand
                        disabled={!solutionActive || false}
                        title={
                          selectedProject && !canRunModels
                            ? 'Reactivate the project before running a model.'
                            : undefined
                        }
                        onClick={() => handleClick(solution.id)}
                      >
                        New {solution.name.toLowerCase()} model
                      </StandardButtonBrand>
                    </SolutionPoster>
                  )
                })}
            </SolutionPosterContainer>
          </SimpleAccordion.Panel>
          <SimpleAccordion
            id={'modelListPageAccordion'}
            defaultActiveKey={['1']}
          >
            <SimpleAccordion.Panel eventKey="1" noCollapse>
              <VersatileGrid
                isSearchable={false}
                gridContainerStyle={{
                  gridTemplateColumns: '1.5fr 0.6fr 0.6fr 0.6fr 1fr 1fr',
                }}
                headers={headers}
                data={getFlattenedArtifactsData()}
                renderRow={(rowData) => [
                  <GridCellBoldText>{rowData.name}</GridCellBoldText>,
                  <GridCellNormalText>
                    <VerySmallNormalTag>
                      {rowData.project_solution?.solution.name}
                    </VerySmallNormalTag>
                  </GridCellNormalText>,
                  <GridCellSmallFadedText>
                    {new Date(rowData?.run_at).toISOString().slice(0, 10)}
                  </GridCellSmallFadedText>,
                  <GridCellNormalText>{rowData?.fit_score}</GridCellNormalText>,
                  <GridCellNormalText>
                    {rowData?.created_by?.name || rowData.created_by}
                  </GridCellNormalText>,
                  <ActionButtonsContainer>
                    <CustomIcons.PenIcon
                      color={theme.colors.fadedText}
                      size="xs"
                      onClick={() => handleEdit(rowData)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span
                      title={
                        canRunModels
                          ? undefined
                          : 'Reactivate the project before running a model.'
                      }
                    >
                      <CustomIcons.ProcessIcon
                        color={theme.colors.fadedText}
                        size="xs"
                        disabled={!canRunModels}
                        onClick={() => handleRun(rowData.id)}
                        style={{
                          cursor: canRunModels ? 'pointer' : 'not-allowed',
                        }}
                      />
                    </span>
                    <CustomIcons.faCopy
                      onClick={() => handleDuplicate(rowData?.id)}
                      color={
                        isDuplicating
                          ? theme.colors.disabled
                          : theme.colors.fadedText
                      }
                      solid={false}
                      size="xs"
                      style={{
                        cursor: isDuplicating ? 'not-allowed' : 'pointer',
                        opacity: isDuplicating ? 0.6 : 1,
                      }}
                    />
                    <CustomIcons.TrashIcon
                      color={theme.colors.fadedText}
                      size="xs"
                      onClick={() => handleDeleteClick(rowData)}
                      style={{ cursor: 'pointer' }}
                    />
                  </ActionButtonsContainer>,
                ]}
                headerInfo="Quick access: Last five saved models"
                maxDisplayedItems={5}
                loading={loading}
                loadingMessage={'Loading models...'}
              />
            </SimpleAccordion.Panel>
          </SimpleAccordion>
          <ModelLoadingModal
            open={run}
            progress={progress}
            handleClose={handleClose}
          />

          {selectedModelToDelete && (
            <DeleteModel
              model={selectedModelToDelete}
              setRefresh={setRefresh}
              open={deleteModalOpen}
              onClose={handleCloseDeleteModal}
            />
          )}
        </SimpleAccordion>
      }
    </>
  )
}

export default NewModelPage
